import type {
  IDepositHistoryTx,
  TDepositHistoryStatus,
} from "@/context/GummiwormContext/hooks/useDepositTxs";

/**
 * Pure deposit lifecycle predicates, shared by the reconciler in
 * GummiwormProvider and the DepositOverlay timeline UI.
 *
 * Terminology: an L2 block is "hard confirmed" once the head peers have
 * multisigned it. Two sources can tell us:
 *
 * 1. The unified API's hydrozoa proxy: GET /head/requests/{id} (hydrozoa
 *    data, proxied by api/src/hydrozoa.rs — the frontend never talks to
 *    hydrozoa directly) reports status.type === "HARD_CONFIRMED" (see
 *    polledStatusUpdate below). This is the authoritative source in the
 *    current any-remote deployment.
 * 2. The websocket ledger view (deposit.refundTx, set on Event::BlockConfirmed).
 *    NOTE: the hydrozoa↔ledger wire contract currently omits
 *    ProxyBlockConfirmation (types/src/types/coordination/command.rs), so this
 *    path never fires today — it is kept for when the contract gains it.
 *
 * A refund effect can be polled before the deposit's block is hard confirmed,
 * so the L1 deposit tx must never be submitted on the strength of a polled
 * refund alone.
 */

/** Statuses in which no further L1 submission work should happen. */
const SUBMISSION_TERMINAL_STATUSES: TDepositHistoryStatus[] = [
  "settling",
  "on-chain",
  "absorbed",
  "failed",
  "rejected",
  "refunded",
  "refunding",
];

export type TStageState = "done" | "active" | "waiting";

export interface IDepositStageStates {
  approved: TStageState;
  refund: TStageState;
  submitted: TStageState;
  available: TStageState;
}

type TStageInput = Pick<
  IDepositHistoryTx,
  "status" | "hardConfirmed" | "refundCbor" | "refundTxHash"
>;

/** The deposit's L2 block has been hard confirmed (multisigned by the head peers). */
export function isHardConfirmed(
  tx: Pick<IDepositHistoryTx, "status" | "hardConfirmed">,
): boolean {
  // "confirmed" is only ever set from the websocket BlockConfirmed path, so
  // it doubles as a hard-confirmation signal for entries persisted before
  // the explicit flag existed. "absorbed" implies confirmation by protocol.
  return (
    tx.hardConfirmed === true ||
    tx.status === "confirmed" ||
    tx.status === "absorbed"
  );
}

/** We hold the multisigned refund tx (cbor for download, hash for display). */
export function hasProvisionedRefund(
  tx: Pick<IDepositHistoryTx, "refundCbor" | "refundTxHash">,
): boolean {
  return !!tx.refundCbor && !!tx.refundTxHash;
}

/**
 * Fallback signature prompt. Deposits sign at click time (the signed tx is
 * held locally; the refund keys off the tx body hash, which witnesses do
 * not change, and nothing reaches L1 until the refund is in hand). This
 * predicate only fires for entries persisted by older builds that deferred
 * the signature until the refund arrived.
 */
export function shouldRequestSignature(tx: IDepositHistoryTx): boolean {
  return (
    !tx.cbor &&
    !!tx.unsignedCbor &&
    !!tx.refundCbor &&
    !SUBMISSION_TERMINAL_STATUSES.includes(tx.status)
  );
}

/**
 * Should the reconciler submit the signed deposit tx to L1 now?
 * Requires BOTH hard confirmation of the deposit's L2 block AND the refund
 * tx in hand, in addition to a collected signature.
 */
export function shouldSubmitDeposit(tx: IDepositHistoryTx): boolean {
  return (
    !!tx.cbor &&
    !!tx.refundCbor &&
    isHardConfirmed(tx) &&
    !tx.hash &&
    !SUBMISSION_TERMINAL_STATUSES.includes(tx.status)
  );
}

/**
 * Turn a polled GET /head/requests/{id} response into a deposit update.
 * Scoped to what only hydrozoa's HTTP status can tell us — hard confirmation
 * and (via the caller) the refund tx. Absorption and rejection are NOT read
 * here: those flow over the websocket ledger view (absorbedInBlock /
 * rejectedInBlock, carried by ApplyDepositDecisions, which the wire contract
 * does include), so reading them here too would double-source them.
 * Only an explicit HARD_CONFIRMED counts — soft confirmation is not enough to
 * release the L1 submission. Returns null when there is nothing to apply.
 */
export function polledStatusUpdate(
  currentStatus: TDepositHistoryStatus,
  polled: { status?: { type?: string } } | null | undefined,
): Partial<IDepositHistoryTx> | null {
  if (polled?.status?.type !== "HARD_CONFIRMED") return null;
  const update: Partial<IDepositHistoryTx> = { hardConfirmed: true };
  // Promote only pre-confirmation statuses; never downgrade an entry that
  // has already progressed (settling, on-chain, refund flow, ...).
  if (currentStatus === "new" || currentStatus === "pending") {
    update.status = "confirmed";
  }
  return update;
}

/**
 * Status update for a deposit the ledger reports as rejected. Hash-aware: the
 * refund tx spends the L1 deposit UTxO, so it only makes sense once the
 * deposit was actually submitted (hash set). A deposit rejected before it
 * ever reached L1 has nothing to refund — mark it failed so the reconciler
 * does not steer it into the refund-submission flow (which would try to spend
 * a UTxO that never existed, and surface a spurious "Refunding" status).
 */
export function rejectionUpdate(
  tx: Pick<IDepositHistoryTx, "hash">,
  failureReason?: string | null,
): Partial<IDepositHistoryTx> {
  if (tx.hash) {
    return {
      status: "rejected",
      errorMessage: failureReason ?? "Deposit was rejected by the network.",
    };
  }
  return {
    status: "failed",
    errorMessage:
      failureReason ??
      "Deposit was rejected before the L1 transaction was submitted.",
  };
}

/**
 * Should the poller keep hitting GET /head/requests/{id} for this deposit?
 * We need hydrozoa's HTTP status until we hold the refund tx AND (for deposits
 * still awaiting confirmation) the hard-confirmation signal. Absorption is not
 * polled — it arrives over the websocket — so polling stops once the refund
 * is in hand and the block is hard confirmed.
 */
export function needsRequestPolling(tx: IDepositHistoryTx): boolean {
  if (["absorbed", "refunded", "refunding", "failed"].includes(tx.status)) {
    return false;
  }
  if (tx.status === "rejected") return !tx.refundCbor;
  const needsRefund = !tx.refundCbor;
  const needsConfirmation =
    !isHardConfirmed(tx) && (tx.status === "new" || tx.status === "pending");
  return needsRefund || needsConfirmation;
}

/**
 * Per-stage timeline states for the deposit modal.
 *
 * "Approved" and "Refund provisioned" are independent prerequisites: the
 * refund can be polled from hydrozoa before the block is hard confirmed,
 * and hard confirmation can beat the poller. "Submitted" only starts once
 * both are green (matching the submission gate above).
 */
export function getDepositStageStates(tx: TStageInput): IDepositStageStates {
  if (tx.status === "absorbed") {
    return { approved: "done", refund: "done", submitted: "done", available: "done" };
  }
  const confirmed = isHardConfirmed(tx);
  const refundReady = hasProvisionedRefund(tx);
  const submittedDone = tx.status === "on-chain";
  return {
    approved: confirmed ? "done" : "active",
    refund: refundReady ? "done" : confirmed ? "active" : "waiting",
    submitted: submittedDone
      ? "done"
      : tx.status === "settling" || (confirmed && refundReady)
        ? "active"
        : "waiting",
    available: submittedDone ? "active" : "waiting",
  };
}
